<template>
  <div class="lp-root">

    <!-- ═══════════════════════════  NAVBAR  ══════════════════════════════ -->
    <header class="lp-navbar" :class="{ 'lp-navbar--scrolled': scrolled }">
      <div class="lp-container lp-navbar__inner">
        <img src="@/assets/images/logo/mexico_verde2.png" alt="Portal México Verde" class="lp-navbar__logo" />
        <nav class="lp-navbar__links">
          <a href="#que-es">¿Qué es?</a>
          <a href="#perfiles">Perfiles</a>
          <a href="#caracteristicas">Características</a>
          <a href="#diagnostico" class="lp-navbar__diag-link">Diagnóstico</a>
          <a href="#blog">Blog</a>
          <a href="#contacto">Contacto</a>
        </nav>
        <div class="lp-navbar__actions">
          <router-link to="/auth/login" class="lp-btn lp-btn--outline-light">Ingresar</router-link>
          <router-link to="/auth/register" class="lp-btn lp-btn--primary">Registrarse</router-link>
        </div>
        <!-- Hamburger mobile -->
        <button class="lp-navbar__hamburger" @click="mobileOpen = !mobileOpen" aria-label="Menú">
          <span></span><span></span><span></span>
        </button>
      </div>
      <!-- Mobile menu -->
      <div class="lp-navbar__mobile" v-if="mobileOpen">
        <a href="#que-es" @click="mobileOpen=false">¿Qué es?</a>
        <a href="#perfiles" @click="mobileOpen=false">Perfiles</a>
        <a href="#caracteristicas" @click="mobileOpen=false">Características</a>
        <a href="#diagnostico" @click="mobileOpen=false">Diagnóstico</a>
        <a href="#blog" @click="mobileOpen=false">Blog</a>
        <a href="#contacto" @click="mobileOpen=false">Contacto</a>
        <router-link to="/auth/login" @click="mobileOpen=false">Ingresar</router-link>
        <router-link to="/auth/register" @click="mobileOpen=false">Registrarse</router-link>
      </div>
    </header>

    <!-- ═══════════════════════════  HERO  ════════════════════════════════ -->
    <section class="lp-hero">
      <div class="lp-hero__overlay"></div>
      <div class="lp-container lp-hero__content">
        <span class="lp-hero__eyebrow">Recuperación Verde en México</span>
        <h1 class="lp-hero__title">Portal México Verde</h1>
        <p class="lp-hero__subtitle">
          Herramienta estratégica para impulsar la transición hacia una economía verde en México,
          articulando gobierno, empresas líderes, inversionistas y MiPyMEs.
        </p>
        <div class="lp-hero__cta">
          <router-link to="/auth/login" class="lp-btn lp-btn--primary lp-btn--lg">Acceder a la plataforma</router-link>
          <a href="#que-es" class="lp-btn lp-btn--ghost lp-btn--lg">Conocer más</a>
        </div>
        <div class="lp-hero__badges">
          <span>Gobierno</span>
          <span>Inversionistas</span>
          <span>Empresas Tractoras</span>
          <span>MiPyMEs</span>
        </div>
      </div>
      <div class="lp-hero__scroll-indicator">
        <span></span>
      </div>
    </section>

    <!-- ═══════════════════════════  QUÉ ES  ══════════════════════════════ -->
    <section id="que-es" class="lp-section lp-section--light">
      <div class="lp-container">
        <div class="lp-section__header">
          <span class="lp-eyebrow">Sobre la plataforma</span>
          <h2>Una plataforma para la transformación verde</h2>
          <p>
            Portal México Verde centraliza el acceso a información, herramientas de diagnóstico,
            directorios de servicios y contenido estratégico para los actores clave del ecosistema
            de reverdecimiento empresarial en México.
          </p>
        </div>
        <div class="lp-metrics">
          <div class="lp-metric" v-for="m in metrics" :key="m.label">
            <strong>{{ m.value }}</strong>
            <span>{{ m.label }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════  PERFILES  ════════════════════════════ -->
    <section id="perfiles" class="lp-section lp-section--dark">
      <div class="lp-container">
        <div class="lp-section__header lp-section__header--light">
          <span class="lp-eyebrow lp-eyebrow--light">Acceso personalizado</span>
          <h2>Un portal adaptado a cada perfil</h2>
          <p>Cada tipo de usuario accede a las herramientas y la información relevante para su rol en el ecosistema verde.</p>
        </div>
        <div class="lp-profiles">
          <div class="lp-profile-card" v-for="p in profiles" :key="p.title">
            <div class="lp-profile-card__icon">
              <vue-feather :type="p.icon" />
            </div>
            <h3>{{ p.title }}</h3>
            <p>{{ p.desc }}</p>
            <ul>
              <li v-for="f in p.features" :key="f">{{ f }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════  CARACTERÍSTICAS  ═════════════════════ -->
    <section id="caracteristicas" class="lp-section lp-section--light">
      <div class="lp-container">
        <div class="lp-section__header">
          <span class="lp-eyebrow">Funcionalidades</span>
          <h2>Todo lo que necesitas en un solo lugar</h2>
        </div>
        <div class="lp-features">
          <div class="lp-feature" v-for="f in features" :key="f.title">
            <div class="lp-feature__icon">
              <vue-feather :type="f.icon" size="28" />
            </div>
            <div>
              <h4>{{ f.title }}</h4>
              <p>{{ f.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════  DIAGNÓSTICO  ═══════════════════════════ -->
    <section id="diagnostico" class="lp-section lp-section--diag">
      <div class="lp-container">
        <div class="lp-diag">
          <div class="lp-diag__content">
            <span class="lp-eyebrow lp-eyebrow--green">Primer paso · Gratuito</span>
            <h2>¿En qué nivel de reverdecimiento está tu empresa?</h2>
            <p>Responde 20 preguntas sobre las prácticas de tu empresa y obtén un diagnóstico instantáneo. No se requiere registro.</p>
            <div class="lp-diag__steps">
              <div class="lp-diag__step" v-for="s in diagSteps" :key="s.num">
                <div class="lp-diag__step-num">{{ s.num }}</div>
                <p>{{ s.text }}</p>
              </div>
            </div>
            <router-link to="/diagnostico" class="lp-btn lp-btn--primary lp-btn--lg">
              Comenzar diagnóstico gratuito →
            </router-link>
          </div>
          <div class="lp-diag__visual">
            <p class="lp-diag__visual-label">Niveles de madurez verde</p>
            <div class="lp-diag__levels">
              <div class="lp-diag__level" v-for="n in nivelesDiag" :key="n.num">
                <span class="lp-diag__level-name">{{ n.label }}</span>
                <div class="lp-diag__level-track">
                  <div class="lp-diag__level-fill" :style="{ width: n.width }"></div>
                </div>
              </div>
            </div>
            <p class="lp-diag__visual-cta">¿Dónde se encuentra tu empresa?</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════  BLOG  ══════════════════════════════════ -->
    <section id="blog" class="lp-section lp-section--light">
      <div class="lp-container">
        <div class="lp-section__header">
          <span class="lp-eyebrow">Noticias y actualidad</span>
          <h2>Blog México Verde</h2>
          <p>Últimas publicaciones sobre economía verde, oportunidades e instrumentos de política en México.</p>
        </div>

        <div v-if="blogLoading" class="lp-blog__loading">
          <div class="lp-blog__spinner"></div>
        </div>

        <p v-else-if="blogError" class="lp-blog__error">
          No fue posible cargar las publicaciones en este momento.
        </p>

        <div v-else class="lp-blog__grid">
          <article v-for="post in blogPosts" :key="post.id" class="lp-blog__card">
            <router-link :to="'/blog/' + post.id" class="lp-blog__img-wrap">
              <img
                v-if="post.thumbnail_rendition"
                :src="post.thumbnail_rendition.full_url"
                :alt="post.thumbnail_rendition.alt || post.title"
                class="lp-blog__img"
              />
              <div v-else class="lp-blog__img lp-blog__img--placeholder"></div>
            </router-link>
            <div class="lp-blog__body">
              <span class="lp-blog__date">{{ formatDate(post.meta.first_published_at) }}</span>
              <h3 class="lp-blog__title">
                <router-link :to="'/blog/' + post.id">{{ post.title }}</router-link>
              </h3>
              <p class="lp-blog__intro">{{ truncate(post.intro, 120) }}</p>
              <router-link :to="'/blog/' + post.id" class="lp-blog__link">
                Leer más →
              </router-link>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════  CTA CENTRAL  ═════════════════════════ -->
    <section class="lp-section lp-cta-band">
      <div class="lp-container lp-cta-band__inner">
        <div>
          <h2>¿Listo para sumarte al cambio?</h2>
          <p>Regístrate en la plataforma y comienza a aprovechar todas las herramientas del Portal México Verde.</p>
        </div>
        <div class="lp-cta-band__actions">
          <router-link to="/auth/register" class="lp-btn lp-btn--primary lp-btn--lg">Crear cuenta</router-link>
          <router-link to="/auth/login" class="lp-btn lp-btn--outline-light lp-btn--lg">Ya tengo cuenta</router-link>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════  FOOTER  ══════════════════════════════ -->
    <footer id="contacto" class="lp-footer">
      <div class="lp-container lp-footer__inner">
        <div class="lp-footer__brand">
          <svg class="lp-footer__svg-logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 280 82" role="img" aria-label="AMSDE – Asociación Mexicana de Secretarios de Desarrollo Económico A.C.">
            <!-- Globe -->
            <circle cx="40" cy="41" r="30" fill="#1565C0"/>
            <!-- Latitude lines -->
            <ellipse cx="40" cy="41" rx="30" ry="11" fill="none" stroke="rgba(255,255,255,0.45)" stroke-width="1"/>
            <ellipse cx="40" cy="28" rx="28" ry="8" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="0.8"/>
            <ellipse cx="40" cy="54" rx="28" ry="8" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="0.8"/>
            <!-- Meridian -->
            <ellipse cx="40" cy="41" rx="13" ry="30" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="0.8"/>
            <line x1="10" y1="41" x2="70" y2="41" stroke="rgba(255,255,255,0.3)" stroke-width="0.8"/>
            <!-- Red curved arrow from lower-left sweeping to upper-right -->
            <path d="M 20,65 C 2,48 4,14 36,8" stroke="#D32F2F" stroke-width="4.5" fill="none" stroke-linecap="round"/>
            <!-- Arrow head -->
            <polygon points="36,8 27,13 31,23" fill="#D32F2F"/>
            <!-- AMSDE text -->
            <text x="85" y="40" font-family="'Arial Black',Arial,sans-serif" font-weight="900" font-size="27" fill="white" letter-spacing="1">AMSDE</text>
            <!-- Subtitle -->
            <text x="85" y="57" font-family="Arial,sans-serif" font-size="10.5" fill="rgba(255,255,255,0.65)">Asociación Mexicana de Secretarios de</text>
            <text x="85" y="71" font-family="Arial,sans-serif" font-size="10.5" fill="rgba(255,255,255,0.65)">Desarrollo Económico A.C.</text>
          </svg>
          <p>Plataforma institucional para la transición hacia una economía verde en México.</p>
        </div>
        <div class="lp-footer__links">
          <h5>Plataforma</h5>
          <a href="#que-es">¿Qué es?</a>
          <a href="#perfiles">Perfiles de acceso</a>
          <a href="#caracteristicas">Características</a>
        </div>
        <div class="lp-footer__links">
          <h5>Acceso</h5>
          <router-link to="/auth/login">Ingresar</router-link>
          <router-link to="/auth/register">Registrarse</router-link>
        </div>
        <div class="lp-footer__links">
          <h5>Contacto</h5>
          <a href="mailto:contacto@mexicoverde.mx">contacto@mexicoverde.mx</a>
          <span>Región México</span>
        </div>
      </div>
      <div class="lp-footer__bottom">
        <span>© {{ currentYear }} Portal México Verde · AMSDE. Todos los derechos reservados.</span>
      </div>
    </footer>

  </div>
</template>

<script>
import axios from 'axios';
import VueFeather from 'vue-feather';

export default {
  name: "LandingPage",
  components: { VueFeather },
  data() {
    return {
      scrolled: false,
      mobileOpen: false,
      currentYear: new Date().getFullYear(),
      blogPosts: [],
      blogLoading: false,
      blogError: false,
      diagSteps: [
        { num: 1, text: 'Responde el cuestionario de reverdecimiento empresarial' },
        { num: 2, text: 'Obtén tu nivel de madurez verde de forma instantánea' },
        { num: 3, text: 'Regístrate y accede a tu hoja de ruta personalizada' },
      ],
      nivelesDiag: [
        { num: 1, label: 'Nivel 1 – Inicial',           width: '15%'  },
        { num: 2, label: 'Nivel 2 – Transición',         width: '35%'  },
        { num: 3, label: 'Nivel 3 – Consolidación',      width: '57%'  },
        { num: 4, label: 'Nivel 4 – Avanzado',           width: '78%'  },
        { num: 5, label: 'Nivel 5 – Referente Verde',    width: '100%' },
      ],
      metrics: [
        { value: '4',       label: 'Actores clave participantes' },
        { value: 'México',  label: 'Región de impacto' },
        { value: '360°',    label: 'Visión del ecosistema verde' },
        { value: '1 portal', label: 'Acceso unificado' },
      ],
      profiles: [
        {
          icon: 'flag',
          title: 'Gobierno',
          desc: 'Acceso estratégico a indicadores, publicaciones y la hoja de ruta de reverdecimiento regional.',
          features: ['Hojas de Ruta Subnacionales y visualizaciones', 'Nuevos programas e incentivos ', 'Evaluaciones'],
        },
        {
          icon: 'trending-up',
          title: 'Inversionista',
          desc: 'Identifica oportunidades de inversión en empresas y proyectos de economía verde.',
          features: ['Oportunidades de inversión', 'Directorio de empresas', 'Biblioteca técnica'],
        },
        {
          icon: 'zap',
          title: 'Empresa Tractora',
          desc: 'Gestiona tu cadena de valor verde e impulsa a tus proveedores MiPyMEs.',
          features: ['Diagnóstico de reverdecimiento', 'Perfil empresarial', 'Oportunidades de encadenamiento'],
        },
        {
          icon: 'briefcase',
          title: 'MiPyMEs',
          desc: 'Herramientas para medir, planificar y acelerar tu transición hacia prácticas empresariales verdes.',
          features: ['Boletín informativo', 'Diagnóstico personalizado', 'Hoja de ruta', 'Registro empresarial', 'Directorios'],
        }        
      ],
      features: [
        { icon: 'activity',   title: 'Diagnóstico de reverdecimiento', desc: 'Evalúa el nivel de madurez verde de tu empresa con un cuestionario guiado.' },
        { icon: 'map',        title: 'Hoja de ruta',                   desc: 'Visualiza el camino estratégico hacia la sostenibilidad empresarial.' },
        { icon: 'map-pin',    title: 'Región Portal México Verde',      desc: 'Explora el panorama territorial de la economía verde en México.' },
        { icon: 'list',       title: 'Directorios',                     desc: 'Encuentra proveedores de servicios verdes y empresas certificadas.' },
        { icon: 'book-open',  title: 'Biblioteca',                      desc: 'Accede a documentos técnicos, guías y normativa ambiental.' },
        { icon: 'file-text',  title: 'Boletín informativo',             desc: 'Mantente al día con noticias y publicaciones del Portal México Verde.' },
      ],
    };
  },
  created() {
    this.fetchBlog();
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    onScroll() {
      this.scrolled = window.scrollY > 60;
    },

    async fetchBlog() {
      this.blogLoading = true;
      this.blogError = false;
      try {
        const { data } = await axios.get(
          'https://rverdeadmin.klatus.systems/api/v2/blog/',
          {
            params: {
              child_of: 4,
              fields: 'intro,body,footer,thumbnail,thumbnail_rendition',
              order: '-first_published_at',
              limit: 3,
            },
          }
        );
        this.blogPosts = data.items ?? [];
        sessionStorage.setItem('_blog_cache', JSON.stringify(this.blogPosts));
      } catch {
        this.blogError = true;
      } finally {
        this.blogLoading = false;
      }
    },

    formatDate(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleDateString('es-MX', {
        day: 'numeric', month: 'short', year: 'numeric',
      });
    },

    truncate(text, max) {
      if (!text || text.length <= max) return text;
      return text.slice(0, max).trimEnd() + '…';
    },
  },
};
</script>

<style scoped>
/* ── Variables ─────────────────────────────────────────────────────────── */
:root {
  --lp-navy:   #09377c;
  --lp-green:  #2e7d32;
  --lp-accent: #43a047;
  --lp-gray:   #f5f7fa;
  --lp-text:   #1a1a2e;
  --lp-muted:  #666;
}

/* ── Reset ─────────────────────────────────────────────────────────────── */
.lp-root { font-family: 'Segoe UI', system-ui, sans-serif; color: #1a1a2e; }
.lp-container { max-width: 1140px; margin: 0 auto; padding: 0 1.5rem; }

/* ── Buttons ───────────────────────────────────────────────────────────── */
.lp-btn {
  display: inline-flex; align-items: center; gap: .4rem;
  padding: .6rem 1.4rem; border-radius: 4px; font-weight: 600;
  font-size: .9rem; text-decoration: none; transition: all .2s; border: 2px solid transparent;
  cursor: pointer;
}
.lp-btn--primary        { background: #AF1F23; color: #fff; border-color: #AF1F23; }
.lp-btn--primary:hover  { background: #8e1519; border-color: #8e1519; }
.lp-btn--outline        { background: transparent; color: #09377c; border-color: #09377c; }
.lp-btn--outline:hover  { background: #09377c; color: #fff; }
.lp-btn--ghost          { background: rgba(255,255,255,.12); color: #fff; border-color: rgba(255,255,255,.4); }
.lp-btn--ghost:hover    { background: rgba(255,255,255,.22); }
.lp-btn--outline-light        { background: transparent; color: #fff; border-color: rgba(255,255,255,.6); }
.lp-btn--outline-light:hover  { background: rgba(255,255,255,.12); }
.lp-btn--lg { padding: .8rem 2rem; font-size: 1rem; }

/* ── Eyebrow ───────────────────────────────────────────────────────────── */
.lp-eyebrow {
  display: block; text-transform: uppercase; letter-spacing: .12em;
  font-size: .75rem; font-weight: 700; color: #AF1F23; margin-bottom: .5rem;
}
.lp-eyebrow--light { color: #81c784; }

/* ── Navbar ────────────────────────────────────────────────────────────── */
.lp-navbar {
  position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
  background: transparent; transition: background .3s, box-shadow .3s;
}
.lp-navbar--scrolled { background: #09377c; box-shadow: 0 2px 16px rgba(0,0,0,.25); }
.lp-navbar__inner {
  display: flex; align-items: center; gap: 2rem;
  height: 68px;
}
.lp-navbar__logo { height: 36px; object-fit: contain; filter: brightness(0) invert(1); }
.lp-navbar__links { display: flex; gap: 1.8rem; margin-left: auto; }
.lp-navbar__links a {
  color: rgba(255,255,255,.85); font-size: .9rem; text-decoration: none;
  font-weight: 500; transition: color .2s;
}
.lp-navbar__links a:hover { color: #fff; }
.lp-navbar__actions { display: flex; gap: .75rem; margin-left: 1.5rem; }
.lp-navbar__hamburger {
  display: none; flex-direction: column; gap: 5px;
  background: none; border: none; cursor: pointer; padding: .4rem; margin-left: auto;
}
.lp-navbar__hamburger span { display: block; width: 24px; height: 2px; background: #fff; border-radius: 2px; }
.lp-navbar__mobile {
  background: #0b2240; padding: 1rem 1.5rem; display: flex; flex-direction: column; gap: .75rem;
}
.lp-navbar__mobile a { color: rgba(255,255,255,.85); text-decoration: none; font-size: .95rem; }

/* ── Hero ──────────────────────────────────────────────────────────────── */
.lp-hero {
  position: relative; min-height: 100vh;
  background: linear-gradient(135deg, #0b1f3a 0%, #09377c 55%, #1a5276 100%);
  display: flex; align-items: center;
}
.lp-hero__overlay {
  position: absolute; inset: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}
.lp-hero__content {
  position: relative; z-index: 1; padding: 6rem 1.5rem 4rem;
  max-width: 760px;
}
.lp-hero__eyebrow {
  display: inline-block; background: rgba(175,31,35,.25); color: #fff;
  border: 1px solid rgba(175,31,35,.5); padding: .25rem .9rem;
  border-radius: 20px; font-size: .78rem; font-weight: 700;
  letter-spacing: .1em; text-transform: uppercase; margin-bottom: 1.25rem;
}
.lp-hero__title {
  font-size: clamp(2.2rem, 5vw, 3.6rem); font-weight: 800;
  color: #fff; line-height: 1.15; margin: 0 0 1.25rem;
}
.lp-hero__subtitle {
  font-size: 1.15rem; color: rgba(255,255,255,.78); line-height: 1.7;
  max-width: 620px; margin-bottom: 2.25rem;
}
.lp-hero__cta { display: flex; gap: 1rem; flex-wrap: wrap; margin-bottom: 3rem; }
.lp-hero__badges { display: flex; gap: .6rem; flex-wrap: wrap; }
.lp-hero__badges span {
  background: rgba(255,255,255,.15); color: #fff;
  border: 1px solid rgba(255,255,255,.35); border-radius: 3px;
  padding: .25rem .75rem; font-size: .78rem; font-weight: 600;
}
.lp-hero__scroll-indicator {
  position: absolute; bottom: 2rem; left: 50%; transform: translateX(-50%);
  display: flex; flex-direction: column; align-items: center;
}
.lp-hero__scroll-indicator span {
  display: block; width: 1px; height: 48px;
  background: linear-gradient(to bottom, rgba(255,255,255,.5), transparent);
  animation: scrollPulse 2s infinite;
}
@keyframes scrollPulse {
  0%, 100% { opacity: 1; transform: scaleY(1); transform-origin: top; }
  50%       { opacity: .4; transform: scaleY(.6); transform-origin: top; }
}

/* ── Sections ──────────────────────────────────────────────────────────── */
.lp-section { padding: 5rem 0; }
.lp-section--light { background: #f5f7fa; }
.lp-section--dark  { background: #09377c; }
.lp-section__header { text-align: center; max-width: 640px; margin: 0 auto 3.5rem; }
.lp-section__header h2 { font-size: clamp(1.6rem, 3vw, 2.2rem); font-weight: 700; margin: .5rem 0 1rem; }
.lp-section__header p  { color: #555; line-height: 1.7; }
.lp-section__header--light h2 { color: #fff; }
.lp-section__header--light p  { color: rgba(255,255,255,.7); }

/* ── Metrics ───────────────────────────────────────────────────────────── */
.lp-metrics {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1.5rem;
  margin-top: 1rem;
}
.lp-metric {
  background: #fff; border: 1px solid #e2e8f0; border-top: 3px solid #AF1F23; border-radius: 8px;
  padding: 2rem 1.5rem; text-align: center; box-shadow: 0 2px 8px rgba(0,0,0,.05);
}
.lp-metric strong { display: block; font-size: 2rem; font-weight: 800; color: #AF1F23; }
.lp-metric span   { font-size: .85rem; color: #666; margin-top: .25rem; display: block; }

/* ── Profile cards ─────────────────────────────────────────────────────── */
.lp-profiles {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.5rem;
}
.lp-profile-card {
  background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.12);
  border-radius: 8px; padding: 2rem 1.5rem; transition: background .2s, transform .2s;
}
.lp-profile-card:hover { background: rgba(255,255,255,.1); transform: translateY(-3px); }
.lp-profile-card__icon {
  width: 48px; height: 48px; border-radius: 8px;
  background: #fff; display: flex; align-items: center; justify-content: center;
  margin-bottom: 1.25rem; box-shadow: 0 0 0 3px rgba(175,31,35,.25);
}
.lp-profile-card__icon svg { width: 22px; height: 22px; stroke: #AF1F23; }
.lp-profile-card h3 { color: #fff; font-size: 1.05rem; font-weight: 700; margin: 0 0 .5rem; }
.lp-profile-card > p { color: rgba(255,255,255,.65); font-size: .88rem; line-height: 1.6; margin-bottom: 1rem; }
.lp-profile-card ul { list-style: none; padding: 0; margin: 0; }
.lp-profile-card ul li {
  color: rgba(255,255,255,.8); font-size: .82rem; padding: .2rem 0;
  padding-left: 1rem; position: relative;
}
.lp-profile-card ul li::before { content: '→'; position: absolute; left: 0; color: #AF1F23; }

/* ── Features ──────────────────────────────────────────────────────────── */
.lp-features {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 2rem;
}
.lp-feature {
  display: flex; gap: 1.25rem; align-items: flex-start;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 8px;
  padding: 1.75rem; box-shadow: 0 2px 8px rgba(0,0,0,.04);
  transition: box-shadow .2s, transform .2s;
}
.lp-feature:hover { box-shadow: 0 6px 24px rgba(16,46,82,.1); transform: translateY(-2px); }
.lp-feature__icon {
  flex-shrink: 0; width: 52px; height: 52px; border-radius: 8px;
  background: #fdecea; display: flex; align-items: center; justify-content: center;
}
.lp-feature__icon .vue-feather { color: #AF1F23; }
.lp-feature h4 { font-size: .95rem; font-weight: 700; margin: 0 0 .35rem; color: #AF1F23; }
.lp-feature p  { font-size: .85rem; color: #666; line-height: 1.6; margin: 0; }

/* ── CTA band ──────────────────────────────────────────────────────────── */
.lp-cta-band {
  background: linear-gradient(135deg, #1a5276 0%, #09377c 100%);
  padding: 4rem 0;
}
.lp-cta-band__inner {
  display: flex; align-items: center; justify-content: space-between;
  gap: 2rem; flex-wrap: wrap;
}
.lp-cta-band__inner h2 { color: #fff; font-size: 1.7rem; font-weight: 700; margin: 0 0 .5rem; }
.lp-cta-band__inner p  { color: rgba(255,255,255,.7); margin: 0; }
.lp-cta-band__actions  { display: flex; gap: 1rem; flex-shrink: 0; flex-wrap: wrap; }

/* ── Footer ────────────────────────────────────────────────────────────── */
.lp-footer { background: #0b1f3a; color: rgba(255,255,255,.7); padding: 4rem 0 0; }
.lp-footer__inner {
  display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 2.5rem; padding-bottom: 3rem;
}
.lp-footer__svg-logo { width: 210px; height: auto; display: block; margin-bottom: 1rem; }
.lp-footer__brand p  { font-size: .85rem; line-height: 1.7; }
.lp-footer__links { display: flex; flex-direction: column; gap: .6rem; }
.lp-footer__links h5 { color: #fff; font-size: .85rem; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; margin: 0 0 .5rem; }
.lp-footer__links a,
.lp-footer__links span { font-size: .85rem; color: rgba(255,255,255,.55); text-decoration: none; transition: color .2s; }
.lp-footer__links a:hover { color: #fff; }
.lp-footer__bottom {
  border-top: 1px solid rgba(255,255,255,.08); padding: 1.25rem 1.5rem;
  text-align: center; font-size: .8rem; color: rgba(255,255,255,.35);
}

/* ── Diagnóstico section ───────────────────────────────────────────────── */
.lp-section--diag { background: linear-gradient(135deg, #071628 0%, #0b2a1a 100%); }
.lp-diag {
  display: grid; grid-template-columns: 1fr 1fr; gap: 5rem; align-items: center;
}
.lp-diag__content h2 {
  color: #fff; font-size: clamp(1.5rem, 3vw, 2rem); font-weight: 700; margin: .5rem 0 1rem;
}
.lp-diag__content > p { color: rgba(255,255,255,.7); line-height: 1.7; margin-bottom: 2rem; }
.lp-eyebrow--green { color: #66bb6a; }

.lp-diag__steps { display: flex; flex-direction: column; gap: 1.1rem; margin-bottom: 2.5rem; }
.lp-diag__step { display: flex; align-items: flex-start; gap: 1rem; }
.lp-diag__step-num {
  flex-shrink: 0; width: 32px; height: 32px; border-radius: 50%;
  background: #AF1F23; color: #fff; font-weight: 700; font-size: .9rem;
  display: flex; align-items: center; justify-content: center;
}
.lp-diag__step p { color: rgba(255,255,255,.8); font-size: .9rem; margin: 0; line-height: 1.55; padding-top: .35rem; }

.lp-diag__visual-label {
  color: rgba(255,255,255,.45); font-size: .72rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: .1em; margin-bottom: 1.25rem;
}
.lp-diag__levels { display: flex; flex-direction: column; gap: 1rem; }
.lp-diag__level { display: flex; flex-direction: column; gap: .35rem; }
.lp-diag__level-name { color: rgba(255,255,255,.75); font-size: .82rem; }
.lp-diag__level-track {
  height: 8px; background: rgba(255,255,255,.1); border-radius: 4px; overflow: hidden;
}
.lp-diag__level-fill {
  height: 100%; border-radius: 4px;
  background: linear-gradient(90deg, #388e3c, #66bb6a);
}
.lp-diag__visual-cta {
  color: rgba(255,255,255,.3); font-size: .78rem; text-align: right; margin-top: 1.25rem; font-style: italic;
}
.lp-navbar__diag-link { color: #81c784 !important; font-weight: 600 !important; }
.lp-navbar__diag-link:hover { color: #a5d6a7 !important; }

/* ── Blog ──────────────────────────────────────────────────────────────── */
.lp-blog__loading { display: flex; justify-content: center; padding: 3rem 0; }
.lp-blog__spinner {
  width: 36px; height: 36px; border: 3px solid #e2e8f0;
  border-top-color: #09377c; border-radius: 50%;
  animation: blogSpin .8s linear infinite;
}
@keyframes blogSpin { to { transform: rotate(360deg); } }
.lp-blog__error { text-align: center; color: #999; font-size: .9rem; padding: 2rem 0; }

.lp-blog__grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem;
}
.lp-blog__card {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 10px;
  overflow: hidden; display: flex; flex-direction: column;
  box-shadow: 0 2px 8px rgba(0,0,0,.05);
  transition: box-shadow .2s, transform .2s;
}
.lp-blog__card:hover { box-shadow: 0 8px 28px rgba(9,55,124,.12); transform: translateY(-3px); }

.lp-blog__img-wrap { display: block; flex-shrink: 0; }
.lp-blog__img {
  width: 100%; aspect-ratio: 16/9; object-fit: cover; display: block;
}
.lp-blog__img--placeholder {
  background: linear-gradient(135deg, #09377c 0%, #2e7d32 100%);
}
.lp-blog__body { padding: 1.5rem; display: flex; flex-direction: column; flex-grow: 1; }
.lp-blog__date {
  display: block; font-size: .72rem; color: #AF1F23; font-weight: 700;
  text-transform: uppercase; letter-spacing: .06em; margin-bottom: .5rem;
}
.lp-blog__title { font-size: 1rem; font-weight: 700; color: #1a1a2e; margin: 0 0 .6rem; line-height: 1.45; }
.lp-blog__title a { color: inherit; text-decoration: none; transition: color .2s; }
.lp-blog__title a:hover { color: #09377c; }
.lp-blog__intro { font-size: .85rem; color: #666; line-height: 1.65; flex-grow: 1; margin: 0 0 1.1rem; }
.lp-blog__link {
  font-size: .85rem; font-weight: 700; color: #AF1F23;
  text-decoration: none; transition: color .2s; margin-top: auto;
}
.lp-blog__link:hover { color: #8e1519; }

/* ── Responsive ────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .lp-navbar__links,
  .lp-navbar__actions { display: none; }
  .lp-navbar__hamburger { display: flex; }

  .lp-footer__inner { grid-template-columns: 1fr 1fr; }
  .lp-cta-band__inner { flex-direction: column; text-align: center; }
  .lp-cta-band__actions { justify-content: center; }

  .lp-diag { grid-template-columns: 1fr; gap: 2.5rem; }
  .lp-diag__visual { display: none; }

  .lp-blog__grid { grid-template-columns: 1fr; }
}
@media (max-width: 480px) {
  .lp-footer__inner { grid-template-columns: 1fr; }
  .lp-hero__cta { flex-direction: column; }
}
</style>
